/**
 * Walkthrough store — session-only (not persisted).
 * Used for both anonymous guests and first-time real-account users.
 * Tracks the active step and the measured pixel rect of the spotlighted element.
 * advanceStep() resets targetRect to null BEFORE moving to the next step so the
 * overlay enters its "loading/full-dim" state while the incoming screen mounts.
 */

import { create } from 'zustand'
import {
  WALKTHROUGH_STEP_ORDER,
} from '../../create/constants/guestWalkthrough.constants'
import type { WalkthroughStepKey, TargetRect } from '../../create/types/guestWalkthrough.types'

interface GuestWalkthroughState {
  isActive: boolean
  currentStep: WalkthroughStepKey | null
  targetRect: TargetRect | null
  /** Optional proxy action for spotlight taps (Modal blocks underlying touches). */
  overlayProxyAction: (() => void) | null
  /** Set when the sign-in screen triggers guest mode — cleared when friends screen starts the walkthrough. */
  pendingWalkthroughStart: boolean

  startWalkthrough: () => void
  setPendingWalkthroughStart: (value: boolean) => void
  setTargetRect: (rect: TargetRect | null) => void
  setOverlayProxyAction: (fn: (() => void) | null) => void
  /** Reset targetRect first (→ full-dim loading state), then move to the next step. */
  advanceStep: () => void
  dismissWalkthrough: () => void
}

export const useGuestWalkthroughStore = create<GuestWalkthroughState>()((set, get) => ({
  isActive: false,
  currentStep: null,
  targetRect: null,
  overlayProxyAction: null,
  pendingWalkthroughStart: false,

  startWalkthrough: () =>
    set({
      isActive: true,
      currentStep: WALKTHROUGH_STEP_ORDER[0],
      targetRect: null,
      overlayProxyAction: null,
      pendingWalkthroughStart: false,
    }),

  setPendingWalkthroughStart: (value) => set({ pendingWalkthroughStart: value }),

  setTargetRect: (rect) => set({ targetRect: rect }),

  setOverlayProxyAction: (fn) => set({ overlayProxyAction: fn }),

  advanceStep: () => {
    const { currentStep } = get()
    if (currentStep === null) return
    const idx = WALKTHROUGH_STEP_ORDER.indexOf(currentStep)
    const nextStep = WALKTHROUGH_STEP_ORDER[idx + 1]
    if (nextStep === undefined) {
      set({ isActive: false, currentStep: null, targetRect: null, overlayProxyAction: null })
    } else {
      // Reset targetRect first so the overlay shows a full-dim state
      // while the next screen mounts and registers its target.
      set({ targetRect: null, currentStep: nextStep, overlayProxyAction: null })
    }
  },

  dismissWalkthrough: () =>
    set({ isActive: false, currentStep: null, targetRect: null, overlayProxyAction: null }),
}))
